import React from 'react';
import { Link } from 'react-router-dom';

interface TokenQuotaExhaustedModalProps {
  isOpen: boolean;
  onClose?: () => void;
  usedTokens: number;
  totalTokens: number;
  planName?: string;
  blocking?: boolean;
}

export const TokenQuotaExhaustedModal: React.FC<TokenQuotaExhaustedModalProps> = ({
  isOpen,
  onClose,
  usedTokens,
  totalTokens,
  planName = 'Edge (Free)',
  blocking = false
}) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop with blur - positioned below header to keep header accessible */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-md z-[9990]" />

      {/* Modal - centered and non-dismissible, positioned below header */}
      <div
        className="fixed inset-0 z-[9991] flex items-center justify-center p-4"
        onClick={blocking ? undefined : onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="token-quota-modal-title"
      >
        <div
          className="bg-slate-900 border-2 border-amber-500/50 rounded-xl shadow-2xl p-8 max-w-2xl w-full relative"
          onClick={(e) => e.stopPropagation()}
          data-used-tokens={usedTokens}
          data-total-tokens={totalTokens}
        >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-amber-400 text-5xl mb-3">⚠️</div>
          <h3
            id="token-quota-modal-title"
            className="text-2xl font-bold text-white"
          >
            Monthly Token Limit Reached
          </h3>
        </div>

        {/* Content */}
        <div className="space-y-6">
          <p className="text-slate-300 text-center">
            You&apos;ve used all of your tokens included in your{' '}
            <strong className="text-amber-300">{planName}</strong> plan this month.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Left: What token limits apply to */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
              <p className="text-slate-300 text-sm font-medium mb-2">
                Token limits apply to:
              </p>
              <ul className="space-y-1 text-sm text-slate-400 mb-3">
                <li>• Scoring responses</li>
                <li>• Consensus analysis</li>
              </ul>
              <p className="text-slate-400 text-xs">
                Your limit resets monthly on the 1st.
              </p>
            </div>

            {/* Right: Upgrade options */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
              <p className="text-slate-300 text-sm font-medium mb-3">
                Upgrade to get more tokens:
              </p>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="flex items-center justify-between">
                  <span>Edge Plan</span>
                  <span className="text-cyan-400 font-medium">100K/mo</span>
                </li>
                <li className="flex items-center justify-between">
                  <span>Flow Plan</span>
                  <span className="text-cyan-400 font-medium">500K/mo</span>
                </li>
                <li className="flex items-center justify-between">
                  <span>Nexus Plan</span>
                  <span className="text-cyan-400 font-medium">2M/mo</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col gap-3">
            <Link
              to="/signup"
              className="w-full px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-semibold transition-all text-center shadow-lg"
            >
              Upgrade to Flow Plan →
            </Link>
            <Link
              to="/signup"
              className="w-full px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg font-medium transition-colors text-center"
            >
              View All Plans
            </Link>
            {!blocking && onClose && (
              <button
                onClick={onClose}
                className="w-full px-6 py-2 text-slate-400 hover:text-slate-300 text-sm transition-colors"
              >
                Maybe Later
              </button>
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  );
};
